import { useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Card, Btn, Badge, Empty } from "../components/UI";
import { toast } from "sonner";
import {
  Video, VideoOff, Mic, MicOff, PhoneOff, Monitor, Users, Clock3, ShieldCheck, NotebookPen, Copy,
} from "lucide-react";

function useTimer(active) {
  const [sec, setSec] = useState(0);
  useEffect(() => {
    if (!active) return;
    const t = setInterval(() => setSec((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [active]);
  const mm = String(Math.floor(sec / 60)).padStart(2, "0");
  const ss = String(sec % 60).padStart(2, "0");
  return `${mm}:${ss}`;
}

function SessionRoom({ appt, onEnd }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [camOn, setCamOn] = useState(true);
  const [micOn, setMicOn] = useState(true);
  const [camError, setCamError] = useState(false);
  const [notes, setNotes] = useState("");
  const elapsed = useTimer(true);
  const { user } = useAuth();

  useEffect(() => {
    navigator.mediaDevices?.getUserMedia({ video: true, audio: true })
      .then((s) => { streamRef.current = s; if (videoRef.current) videoRef.current.srcObject = s; })
      .catch(() => setCamError(true));
    return () => streamRef.current?.getTracks().forEach((t) => t.stop());
  }, []);

  const toggle = (kind) => {
    const tracks = kind === "video" ? streamRef.current?.getVideoTracks() : streamRef.current?.getAudioTracks();
    (tracks || []).forEach((t) => { t.enabled = !t.enabled; });
    if (kind === "video") setCamOn((v) => !v); else setMicOn((v) => !v);
  };

  const saveAsNote = async () => {
    try {
      await api.post("/progress-notes", {
        client_id: appt.client_id, format: "DAP", service: "Telehealth Session",
        fields: { data: notes }, status: "draft",
      });
      toast.success("Session notes saved as draft progress note");
      setNotes("");
    } catch { toast.error("Could not save note - check the client link"); }
  };

  return (
    <div className="grid gap-5 lg:grid-cols-3" data-testid="telehealth-room">
      <div className="lg:col-span-2">
        <div className="relative overflow-hidden rounded-3xl bg-[#1F1410] aspect-video shadow-[0_24px_60px_-18px_rgba(31,20,16,0.6)]">
          {camError ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-white/60 text-sm gap-2">
              <VideoOff size={32} /> Camera unavailable - check browser permissions
            </div>
          ) : (
            <video ref={videoRef} autoPlay muted playsInline className={`h-full w-full object-cover ${camOn ? "" : "opacity-0"}`} data-testid="telehealth-local-video" />
          )}
          {!camOn && !camError && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="flex h-24 w-24 items-center justify-center rounded-full bg-[#4E342E] text-3xl font-bold text-[#FFB300]">
                {(user?.first_name || "?")[0]}{(user?.last_name || "")[0]}
              </div>
            </div>
          )}
          <div className="absolute top-4 left-4 flex items-center gap-2">
            <span className="rounded-full bg-black/50 px-3 py-1 text-xs font-semibold text-white backdrop-blur flex items-center gap-1.5">
              <Clock3 size={12} /> {elapsed}
            </span>
            <span className="rounded-full bg-[#C62828] px-3 py-1 text-xs font-bold text-white animate-pulse">LIVE</span>
          </div>
          <div className="absolute top-4 right-4 rounded-2xl bg-black/50 backdrop-blur px-3.5 py-2 text-white text-xs">
            <div className="font-semibold">{appt.client_name}</div>
            <div className="text-white/60">Waiting to join…</div>
          </div>
          <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex items-center gap-3">
            <button data-testid="telehealth-mic-btn" aria-label="Toggle microphone" onClick={() => toggle("audio")}
              className={`flex h-12 w-12 items-center justify-center rounded-full transition-colors ${micOn ? "bg-white/15 text-white hover:bg-white/25" : "bg-white text-[#C62828]"}`}>
              {micOn ? <Mic size={19} /> : <MicOff size={19} />}
            </button>
            <button data-testid="telehealth-cam-btn" aria-label="Toggle camera" onClick={() => toggle("video")}
              className={`flex h-12 w-12 items-center justify-center rounded-full transition-colors ${camOn ? "bg-white/15 text-white hover:bg-white/25" : "bg-white text-[#C62828]"}`}>
              {camOn ? <Video size={19} /> : <VideoOff size={19} />}
            </button>
            <button data-testid="telehealth-share-btn" aria-label="Share screen" onClick={() => toast.info("Screen sharing arrives with full video infrastructure")}
              className="flex h-12 w-12 items-center justify-center rounded-full bg-white/15 text-white hover:bg-white/25 transition-colors">
              <Monitor size={19} />
            </button>
            <button data-testid="telehealth-end-btn" aria-label="End session" onClick={onEnd}
              className="flex h-12 items-center gap-2 rounded-full bg-[#C62828] px-6 text-sm font-bold text-white hover:bg-[#B71C1C] transition-colors">
              <PhoneOff size={17} /> End
            </button>
          </div>
        </div>
        <p className="mt-3 flex items-center gap-1.5 text-xs text-[#7B6157]"><ShieldCheck size={13} /> Encrypted session shell - peer-to-peer video connects here once telehealth infrastructure is activated.</p>
      </div>
      <Card className="p-5 flex flex-col">
        <div className="mb-3 flex items-center gap-2">
          <div className="h-8 w-8 rounded-xl bg-[#F5F2EB] flex items-center justify-center text-[#7B6157]"><NotebookPen size={15} /></div>
          <h3 className="font-heading text-sm font-bold text-[#3E2723]">Session Notes</h3>
        </div>
        <textarea data-testid="telehealth-notes" value={notes} onChange={(e) => setNotes(e.target.value)}
          placeholder="Type observations during the session… save them as a draft DAP note when you're done."
          className="flex-1 min-h-[220px] w-full resize-none rounded-xl border border-[#E8E2D6] bg-[#FDFCF8] p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#8D6E63]/40" />
        <div className="mt-3 flex gap-2">
          <Btn data-testid="telehealth-save-note-btn" className="flex-1" disabled={!notes.trim()} onClick={saveAsNote}>
            <NotebookPen size={14} /> Save as Draft Note
          </Btn>
          <Btn variant="outline" data-testid="telehealth-copy-note-btn" disabled={!notes.trim()}
            onClick={() => { navigator.clipboard.writeText(notes); toast.success("Copied"); }}><Copy size={14} /></Btn>
        </div>
      </Card>
    </div>
  );
}

export default function Telehealth() {
  const [appts, setAppts] = useState(null);
  const [session, setSession] = useState(null);
  const today = dayjs().format("YYYY-MM-DD");

  useEffect(() => {
    api.get("/appointments", { params: { date: today } }).then((r) => setAppts(r.data)).catch(() => setAppts([]));
  }, [today]);

  if (session) return (
    <div>
      <PageHeader title={`Telehealth - ${session.client_name}`} subtitle={`${session.type || "Session"} · ${session.start_time || ""}`} />
      <SessionRoom appt={session} onEnd={() => setSession(null)} />
    </div>
  );

  return (
    <div data-testid="telehealth-page">
      <PageHeader title="Telehealth" subtitle="Secure video sessions with your clients" />
      <div className="grid gap-5 lg:grid-cols-3">
        <Card className="p-5 lg:col-span-2">
          <div className="mb-3 flex items-center gap-2">
            <div className="h-8 w-8 rounded-xl bg-[#F5F2EB] flex items-center justify-center text-[#7B6157]"><Video size={15} /></div>
            <h3 className="font-heading text-sm font-bold text-[#3E2723]">Today's Sessions</h3>
          </div>
          {!appts ? <div className="py-8 text-center text-xs text-[#84675B]">Loading…</div> :
            appts.length === 0 ? <Empty text="No appointments scheduled today" /> : (
            <div className="space-y-2">
              {appts.map((a) => (
                <div key={a.id} data-testid={`telehealth-appt-${a.id}`} className="flex items-center justify-between rounded-xl border border-[#F1E8DA] bg-[#FDFCF8] px-4 py-3">
                  <div>
                    <div className="text-sm font-semibold text-[#3E2723]">{a.client_name}</div>
                    <div className="text-xs text-[#84675B]">{a.type || "Session"} · {a.start_time} to {a.end_time}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge value={a.status} />
                    <Btn data-testid={`telehealth-start-${a.id}`} className="px-4 py-1.5 text-xs" onClick={() => setSession(a)}>
                      <Video size={13} /> Start Session
                    </Btn>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
        <Card className="p-5">
          <div className="mb-3 flex items-center gap-2">
            <div className="h-8 w-8 rounded-xl bg-[#F5F2EB] flex items-center justify-center text-[#7B6157]"><Users size={15} /></div>
            <h3 className="font-heading text-sm font-bold text-[#3E2723]">Virtual Waiting Room</h3>
          </div>
          <Empty text="No clients waiting - invite links appear here when sessions are shared" />
          <div className="mt-4 rounded-xl bg-[#F9F5EE] border border-[#F1E8DA] p-4 text-xs text-[#7B6157] leading-relaxed">
            <ShieldCheck size={14} className="mb-1 text-[#4E342E]" />
            Telehealth sessions are HIPAA-aligned: encrypted transport, no session recording by default, and client identity verification at join.
          </div>
        </Card>
      </div>
    </div>
  );
}
